// Strongly inspired by https://usehooks-ts.com/react-hook/use-local-storage
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { getLocalStorageItem, setLocalStorageItem } from '@/utility/localStorage';

declare global {
  interface WindowEventMap {
    'local-storage': CustomEvent;
  }
}

const IS_SERVER = typeof window === 'undefined';

const useLocalStorage = <T>(key: string, initialValue: T): [T, Dispatch<SetStateAction<T>>] => {
  const readValue = useCallback(
    (): T => getLocalStorageItem<T>(key) ?? initialValue,
    [initialValue, key]
  );

  const [storedValue, setStoredValue] = useState<T>(initialValue);

  useEffect(() => {
    setStoredValue(getLocalStorageItem<T>(key) ?? initialValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const setValue = useCallback<Dispatch<SetStateAction<T>>>(
    (value) => {
      if (IS_SERVER) {
        console.warn(
          `Tried setting localStorage key “${key}” even though environment is not a client`
        );
        return;
      }

      try {
        const newValue = value instanceof Function ? value(readValue()) : value;

        setStoredValue(newValue);
        setLocalStorageItem(key, newValue);
      } catch (error) {
        console.error(`Error setting localStorage key “${key}”:`, error);
      }
    },
    [key, readValue]
  );

  const handleStorageChange = useCallback(
    (event: StorageEvent | CustomEvent) => {
      if ((event as StorageEvent).key && (event as StorageEvent).key !== key) {
        return;
      }

      setStoredValue(readValue());
    },
    [key, readValue]
  );

  useEffect(() => {
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('local-storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('local-storage', handleStorageChange);
    };
  }, [handleStorageChange]);

  return [storedValue, setValue];
};

export default useLocalStorage;
